import React, { useMemo } from 'react'
import Checkbox from '@mui/material/Checkbox'
import { useTranslation } from 'react-i18next'

interface ListRow<T> {
  data: T
}

type GetSelector<T> = (row: T) => string
interface Options<T> {
  disableCheckboxFor?: (row: T, alreadySelectedRow: T[]) => boolean
}
interface IUseMultiSelection<T> {
  selectedItems: string[]
  selectedRows: T[]
  ListCheckbox: React.FC<ListRow<T>>
  SelectAllCheckbox: any
  areAllSelected: boolean
  refreshSelection: () => void
}

export function useMultiSelection<T>(getSelector: GetSelector<T>, options?: Options<T>): IUseMultiSelection<T> {
  const { t } = useTranslation()
  const [areAllSelected, setAreAllSelected] = React.useState<boolean>(false)
  const [pageItems, setPageItems] = React.useState<{ key: string; row: T }[]>([])
  const [selectedItems, setSelectedItems] = React.useState<string[]>([])
  const [selectedRows, setSelectedRows] = React.useState<T[]>([])

  React.useEffect(() => {
    if (areAllSelected) {
      setSelectedItems(pageItems.map(i => i.key))
      setSelectedRows(pageItems.map(i => i.row))
    } else {
      setSelectedItems([])
      setSelectedRows([])
    }
  }, [areAllSelected])

  const registerItem = (itemUUID: string, row: T): void => {
    setPageItems(prevItems => {
      const alreadyExists = prevItems.some(reg => reg.key === itemUUID)
      if (alreadyExists) {
        return prevItems
      }
      return [...prevItems, { key: itemUUID, row }]
    })
  }

  const onSelect = (e: React.ChangeEvent<HTMLInputElement>, itemUUID: string, row: T) => {
    const isAdd = e.target.checked
    // add the item to the selected, or remove it when it was unchecked
    setSelectedItems(prevItems => [...prevItems, itemUUID].filter(UUID => isAdd || UUID !== itemUUID))
    setSelectedRows(prevItems => [...prevItems, row].filter(rowItem => isAdd || getSelector(rowItem) !== itemUUID))
  }

  const ListCheckbox: React.FC<ListRow<T>> = (row: ListRow<T>) => {
    const itemUUID = getSelector(row.data)
    registerItem(itemUUID, row.data)
    const disabled = useMemo(() => {
      if (!options?.disableCheckboxFor) {
        return false
      }
      return options.disableCheckboxFor(row.data, selectedRows)
    }, [options?.disableCheckboxFor, selectedItems])

    return (
      <Checkbox
        size='small'
        color='primary'
        disabled={disabled}
        checked={selectedItems.some(UUID => UUID === itemUUID)}
        onChange={e => onSelect(e, itemUUID, row.data)}
      />
    )
  }

  const SelectAllCheckbox: React.FC = (): JSX.Element => (
    <Checkbox size='small' color='primary' title={t('global.select_all_from_page')} onChange={e => setAreAllSelected(e.target.checked)} />
  )

  const refreshSelection = () => {
    setAreAllSelected(false)
    setPageItems([])
    setSelectedItems([])
  }

  return React.useMemo(
    () => ({ selectedItems, ListCheckbox, SelectAllCheckbox, areAllSelected, refreshSelection, selectedRows }),
    [selectedItems, ListCheckbox, SelectAllCheckbox, areAllSelected, refreshSelection, selectedRows]
  )
}
